import { ClientLambdaApi } from '@/api-client.js'

const createRun = async (executorName, requestPayload) => {
  const response = await ClientLambdaApi.post('/executor-runs', {
    executor_name: executorName,
    request_payload: requestPayload
  })
  return response.data
}

const getRun = async (runId) => {
  const response = await ClientLambdaApi.get(`/executor-runs/${runId}`)
  return response.data
}

const POLL_TIMEOUT_MS = 15 * 60 * 1000 // 15 minutes

/**
 * Polls a run until it reaches DONE or ERROR status.
 * Rejects with 'timeout' after 15 minutes or 'cancelled' if cancel() is called.
 * Returns { promise, cancel }.
 */
const pollUntilDone = (runId, { onProgress, intervalMs = 2000 } = {}) => {
  let cancelled = false
  let timerId = null
  let timeoutId = null
  let rejectFn = null

  const promise = new Promise((resolve, reject) => {
    rejectFn = reject

    timeoutId = setTimeout(() => {
      cancelled = true
      if (timerId) { clearTimeout(timerId); timerId = null }
      reject(new Error('timeout'))
    }, POLL_TIMEOUT_MS)

    const tick = async () => {
      if (cancelled) return
      try {
        const run = await getRun(runId)
        if (onProgress) onProgress(run)
        if (run.status === 'DONE' || run.status === 'ERROR') {
          clearTimeout(timeoutId)
          resolve(run)
        } else if (!cancelled) {
          timerId = setTimeout(tick, intervalMs)
        }
      } catch (err) {
        if (!cancelled) {
          clearTimeout(timeoutId)
          reject(err)
        }
      }
    }

    tick()
  })

  const cancel = () => {
    if (cancelled) return
    cancelled = true
    if (timerId) { clearTimeout(timerId); timerId = null }
    if (timeoutId) { clearTimeout(timeoutId); timeoutId = null }
    if (rejectFn) rejectFn(new Error('cancelled'))
  }

  return { promise, cancel }
}

/**
 * Creates a run that expects a file upload via presigned S3 URL.
 * Returns { run_id, files: [{ filename, s3_key, presigned_url }] }
 */
const createRunWithUpload = async (executorName, filenames) => {
  const response = await ClientLambdaApi.post('/executor-runs/with-upload', {
    executor_name: executorName,
    filenames
  })
  return response.data
}

/**
 * Uploads a file to S3 using a presigned PUT URL.
 * Does NOT use ClientLambdaApi to avoid injecting Authorization headers.
 */
const uploadToS3 = async (presignedUrl, file, contentType) => {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file
  })
  if (!response.ok) {
    throw new Error(`S3 upload failed: ${response.status}`)
  }
}

export { createRun, getRun, pollUntilDone, createRunWithUpload, uploadToS3 }



// WEBPACK FOOTER //
// ./src/services/executorRunsService.js